// Pipeline debrief — opening screen of the editor session.
// Phase 2 derives every number from the Edition document itself (the raw brief
// is preserved in edition.pipelineMeta). Bench count is left blank until the
// Phase 5 endpoint ships.

import {
  CATEGORY_ORDER,
  CATEGORY_PALETTE,
  CATEGORY_FALLBACK,
} from './lib/categoryPalette.js';
import { categorySpreadEntries, deriveBriefStats } from '../../../lib/briefStats.js';

function formatRelativeTime(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.valueOf())) return '—';
  const diffMs = Date.now() - date.valueOf();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function deriveStats(edition) {
  const base = deriveBriefStats(edition);
  if (!base) return null;
  return {
    ...base,
    importedAt: edition.pipelineImportedAt,
    schemaVersion: edition.pipelineSchemaVersion,
    versionHash: edition.pipelineVersionHash,
  };
}

function CategorySpreadCard({ spread, total }) {
  const entries = categorySpreadEntries(spread);

  // Prefer the caller-supplied total (== articles in the edition) so this card
  // and the "In brief" tile always agree. Fall back to summing the spread if
  // no total was passed.
  const totalCount =
    typeof total === 'number'
      ? total
      : entries.reduce((sum, [, n]) => sum + n, 0);
  const max = entries.reduce((m, [, n]) => Math.max(m, n), 0);

  return (
    <div className="hxd-card">
      <div className="flex items-baseline justify-between mb-4 pb-2.5 border-b-2 border-hxd-orange">
        <div className="font-display text-[11px] font-bold tracking-display-md uppercase">
          Category spread
        </div>
        <div className="font-display text-[10px] font-bold tracking-display-sm uppercase text-hxd-muted">
          {totalCount} {totalCount === 1 ? 'article' : 'articles'}
        </div>
      </div>
      {entries.length === 0 ? (
        <p className="text-[12px] text-hxd-muted">No categories yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {entries.map(([category, count]) => {
            const palette = CATEGORY_PALETTE[category] || CATEGORY_FALLBACK;
            const widthPct = max > 0 ? Math.max(4, (count / max) * 100) : 0;
            const sharePct =
              totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
            return (
              <li
                key={category}
                className="grid items-center gap-3"
                style={{ gridTemplateColumns: '96px minmax(0, 1fr) auto' }}
              >
                <span className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-ink truncate">
                  {category}
                </span>
                <div
                  className="relative h-5 rounded-[2px] overflow-hidden"
                  style={{ backgroundColor: palette.track }}
                  role="img"
                  aria-label={`${category}: ${count} ${count === 1 ? 'article' : 'articles'} (${sharePct}% of today's brief)`}
                >
                  <div
                    className="absolute inset-y-0 left-0 rounded-[2px] transition-[width] duration-300 ease-out"
                    style={{ width: `${widthPct}%`, backgroundColor: palette.bar }}
                  />
                </div>
                <span className="flex items-baseline gap-1.5 justify-end tabular-nums">
                  <span
                    className="font-mono text-[15px] font-bold leading-none"
                    style={{ color: palette.ink }}
                  >
                    {count}
                  </span>
                  <span className="font-mono text-[10px] text-hxd-muted leading-none w-9 text-right">
                    {sharePct}%
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className="hxd-card flex flex-col gap-1.5 min-w-0">
      <div className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted">
        {label}
      </div>
      <div className="font-display text-[28px] font-bold text-hxd-ink leading-none">
        {value ?? '—'}
      </div>
      {sub && <div className="text-[11px] text-hxd-muted">{sub}</div>}
    </div>
  );
}

export default function PipelineDebrief({ edition, onOpenBrief }) {
  if (!edition) return null;
  const stats = deriveStats(edition);
  if (!stats) return null;

  const reviewed = stats.reviewed || 0;
  const inBrief = stats.inBrief || 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange mb-1">
            Pipeline Debrief
          </div>
          <h1 className="hxd-page-title">
            {edition.isoDate} brief — ready for review
          </h1>
          <p className="hxd-page-subtitle">
            Pipeline finished {formatRelativeTime(stats.importedAt)}.
            {stats.versionHash && (
              <span className="ml-1 text-[11px] font-mono text-hxd-muted">
                pipeline {stats.versionHash.slice(0, 7)}
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenBrief}
          className="hxd-btn hxd-btn-primary"
        >
          Open today's brief →
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Reviewed" value={reviewed} sub="ingested from sources" />
        <StatTile
          label="Filtered out"
          value={stats.filteredOut}
          sub="noise & off-keyword"
        />
        <StatTile
          label="Story dupes merged"
          value={stats.storyDupesMerged}
          sub="same story, different outlet"
        />
        <StatTile label="In brief" value={inBrief} sub="today's edition" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <CategorySpreadCard spread={stats.categorySpread} total={inBrief} />

        <div className="hxd-card">
          <div className="hxd-card-header">Top sources</div>
          {stats.topSources.length === 0 ? (
            <p className="text-[12px] text-hxd-muted">No sources surfaced.</p>
          ) : (
            <ul className="space-y-1.5">
              {stats.topSources.map((s) => (
                <li
                  key={s.name}
                  className="flex items-center justify-between text-[13px] text-hxd-ink"
                >
                  <span className="truncate">{s.name}</span>
                  <span className="font-display font-bold text-hxd-orange">×{s.n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
